/**
 * Mensagens amigáveis para erros HTTP comuns das Functions que cobram LLM.
 * Sem isso, o front mostra "Falha (402): <stack técnica>".
 */
export class PaymentRequiredError extends Error {
  constructor() {
    super("Para corrigir, você precisa de uma assinatura ativa.");
    this.name = "PaymentRequiredError";
  }
}

export class ForbiddenError extends Error {
  constructor(message = "Operação não autorizada.") {
    super(message);
    this.name = "ForbiddenError";
  }
}

export class RateLimitError extends Error {
  constructor(message = "Muitas chamadas em pouco tempo. Aguarde alguns segundos.") {
    super(message);
    this.name = "RateLimitError";
  }
}

/**
 * Lê o status e devolve a mensagem certa. 402 → "precisa assinatura", 403 →
 * "não autorizado", outros → texto bruto da Function.
 */
export async function readErrorFromResponse(res: Response): Promise<Error> {
  const txt = await res.text().catch(() => "");
  if (res.status === 402) return new PaymentRequiredError();
  if (res.status === 403) return new ForbiddenError(txt || "Operação não autorizada.");
  if (res.status === 429) return new RateLimitError(txt || undefined);
  return new Error(txt || `Erro ${res.status}`);
}

/**
 * Função de toast com tratamento amigável para PaymentRequiredError.
 * Mostra um botão "Assinar agora" que leva para /precos. Para outros erros,
 * comportamento toast.error padrão.
 *
 * Importe `toast` do "sonner" e passe direto:
 *   showCorrectionError(toast, err, navigate)
 */
type ToastFn = (typeof import("sonner"))["toast"];
type NavigateFn = (path: string) => void;

export function showCorrectionError(
  toast: ToastFn,
  err: unknown,
  navigate: NavigateFn,
  fallback = "Falha ao corrigir.",
): void {
  if (err instanceof PaymentRequiredError) {
    toast.error(err.message, {
      action: {
        label: "Assinar agora",
        onClick: () => navigate("/precos"),
      },
      duration: 8000,
    });
    return;
  }
  if (err instanceof ForbiddenError) {
    toast.error(err.message);
    return;
  }
  if (err instanceof RateLimitError) {
    toast.error(err.message);
    return;
  }
  toast.error(err instanceof Error ? err.message : fallback);
}
